import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

import { LayoutComponent } from '../../../shared/layouts/layout/layout.component';
import { AddUpdateActionPlanComponent } from '../../components/add-update-action-plan/add-update-action-plan.component';
import { ViewTasksComponent } from '../../components/view-tasks/view-tasks.component';
import { ActionPlansService } from '../../services/action-plans/action-plans.service';

@Component({
  selector: 'app-action-plans',
  standalone: true,
  imports: [
    AccordionModule,
    AutoCompleteModule,
    ButtonModule,
    CommonModule,
    DropdownModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    LayoutComponent,
    PaginatorModule,
    ReactiveFormsModule,
    TableModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './action-plans.component.html',
  styleUrl: './action-plans.component.scss'
})
export class ActionPlansComponent implements OnInit, OnDestroy {
  private _actionPlansService = inject(ActionPlansService);
  private _dialogService = inject(DialogService);
  private _messageService = inject(MessageService);
  private _formBuilder = inject(FormBuilder);

  public actionPlans: any[] = [];
  public loading: boolean = false;

  public dataForm!: FormGroup;
  private filterSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  public activeView: 'table' | 'kanban' = 'table';
  public kanbanColumns: any[] = [];
  public page_size: number = 10;

  public rootCauses: any[] = [];
  public qualifications: any[] = [];
  public events: any[] = [];

  private addUpdateDialogRef: DynamicDialogRef | undefined;
  private viewTasksDialogRef: DynamicDialogRef | undefined;

  ngOnInit(): void {
    this.activeView = (localStorage.getItem('activeViewActionPlans') as 'table' | 'kanban') || 'table';
    this.page_size = Number(localStorage.getItem('page_size')) || 10;

    this.loadDataForm();
    this.loadData();

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadActionPlans();
    }

    this.filterSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.activeView === 'kanban') {
        this.loadDataKanban();
      } else {
        this.loadActionPlans();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.addUpdateDialogRef) {
      this.addUpdateDialogRef.close();
    }

    if (this.viewTasksDialogRef) {
      this.viewTasksDialogRef.close();
    }
  }

  onFilterInput(event: any, filter: string) {
    const inputElement = event.target as HTMLInputElement;
    this.dataForm.value[filter] = inputElement.value;

    this.filterSubject.next(inputElement.value);
  }

  resetFilters() {
    this.loadDataForm();

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadActionPlans();
    }
  }

  loadActionPlans() {
    this.loading = true;

    const filters = {
      ...this.dataForm.value,
      root_cause: this.dataForm.value.root_cause instanceof Object ? this.dataForm.value.root_cause.id : this.dataForm.value.root_cause,
      qualification: this.dataForm.value.qualification instanceof Object ? this.dataForm.value.qualification.id : this.dataForm.value.qualification,
      event: this.dataForm.value.event instanceof Object ? this.dataForm.value.event.id : this.dataForm.value.event,
    };

    this._actionPlansService.getActionPlans(filters).subscribe({
      next: (data: any) => {
        this.actionPlans = data.items;

        this.dataForm.patchValue({
          totalRecords: data.total,
          page: data.page,
          page_size: data.page_size
        });

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading action plans:', error);
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los planes de acción'
        });
        this.loading = false;
      }
    });
  }

  loadDataKanban(): void {
    this.loading = true;

    const statusConfig = [
      { id: 'Por hacer', title: 'Por Hacer', color: '#3b82f6' },
      { id: 'Progreso', title: 'En Progreso', color: '#f59e0b' },
      { id: 'Completado', title: 'Completado', color: '#10b981' }
    ];

    this.kanbanColumns = statusConfig.map(status => ({
      statusId: status.id,
      title: status.title,
      color: status.color,
      plans: [],
      loading: false,
      currentPage: 1,
      totalPages: 1,
      total: 0
    }));

    this.kanbanColumns.forEach(column => {
      this.loadKanbanColumnData(column);
    });

    this.loading = false;
  }

  loadKanbanColumnData(column: any, page: number = 1): void {
    column.loading = true;

    const params: any = {
      ...this.dataForm.value,
      page: page,
      page_size: this.page_size,
      status: column.statusId,
      search: this.dataForm.value.search || '',
    };

    this._actionPlansService.getActionPlans(params).subscribe({
      next: (response: any) => {
        if (page === 1) {
          column.plans = response.items;
        } else {
          column.plans = [...column.plans, ...response.items];
        }

        column.currentPage = response.page;
        column.totalPages = Math.ceil(response.total / this.page_size);
        column.total = response.total;
        column.loading = false;
      },
      error: (error) => {
        column.loading = false;
        console.error('Error loading column data:', error);
      }
    });
  }

  loadMoreKanbanItems(column: any): void {
    if (column.currentPage < column.totalPages) {
      this.loadKanbanColumnData(column, column.currentPage + 1);
    }
  }

  toggleView(view: 'table' | 'kanban') {
    this.activeView = view;
    localStorage.setItem('activeViewActionPlans', this.activeView);

    if (view === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadActionPlans();
    }
  }

  loadDataForm(): void {
    this.dataForm = this._formBuilder.group({
      totalRecords: 0,
      page: 1,
      page_size: this.page_size,
      firstPage: 0,
      search: '',
      root_cause: '',
      qualification: '',
      event: '',
    });
  }

  loadData(): void {
    this._actionPlansService.getRootCauses().subscribe((data: any) => {
      this.rootCauses = data.items;
    });

    this._actionPlansService.getQualifications().subscribe((data: any) => {
      this.qualifications = data.items;
    });

    this._actionPlansService.getEvents().subscribe((data: any) => {
      this.events = data.items;
    });
  }

  filterRootCause(event: any) {
    const query = event ? event.query.toLowerCase() : '';

    this._actionPlansService.getRootCauses(query).subscribe((data: any) => {
      this.rootCauses = data.items;
    });
  }

  filterQualification(event: any) {
    const query = event ? event.query.toLowerCase() : '';

    this._actionPlansService.getQualifications(query).subscribe((data: any) => {
      this.qualifications = data.items;
    });
  }

  filterEvent(event: any) {
    const query = event ? event.query.toLowerCase() : '';

    this._actionPlansService.getEvents(query).subscribe((data: any) => {
      this.events = data.items;
    });
  }

  selectField(event: any, field: string) {
    this.dataForm.patchValue({
      [field]: event
    });

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadActionPlans();
    }
  }

  pageChange(event: any) {
    const newPage = event.first / event.rows;

    this.dataForm.patchValue({
      firstPage: event.first,
      page: newPage + 1,
      page_size: event.rows
    });

    localStorage.setItem('page_size', event.rows.toString());
    this.loadActionPlans();
  }

  addUpdateActionPlan(actionPlan: any): void {
    this.addUpdateDialogRef = this._dialogService.open(AddUpdateActionPlanComponent, {
      header: actionPlan ? 'Editar Plan de Acción' : 'Agregar Plan de Acción',
      width: '50%',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: true,
      data: {
        actionPlan: actionPlan
      }
    });

    this.addUpdateDialogRef.onClose.subscribe((result) => {
      if (result) {
        if (this.activeView === 'kanban') {
          this.loadDataKanban();
        } else {
          this.loadActionPlans();
        }
      }
    });
  }

  deleteActionPlan(actionPlan: any): void {
    if (confirm(`¿Está seguro que desea eliminar el plan de acción ${actionPlan.folio}?`)) {
      this._actionPlansService.deleteActionPlan(actionPlan.id).subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: '¡Éxito!',
            detail: 'Plan de acción eliminado correctamente'
          });

          if (this.activeView === 'kanban') {
            this.loadDataKanban();
          } else {
            this.loadActionPlans();
          }
        },
        error: (error) => {
          console.error('Error deleting action plan:', error);
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar el plan de acción'
          });
        }
      });
    }
  }

  viewTasks(actionPlan: any): void {
    this.viewTasksDialogRef = this._dialogService.open(ViewTasksComponent, {
      header: `Tareas del Plan de Acción ${actionPlan.folio}`,
      width: '80%',
      height: '90vh',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: true,
      data: {
        actionPlan: actionPlan
      }
    });
  }
}
